import { cn } from "@apex/shared";
import { Card, CardContent } from "./ui/Card";

export interface SkeletonItemCardProps {
  className?: string;
}

/**
 * SkeletonItemCard — placeholder card matching the dimensions of a real
 * `CatalogItemCard`, used by the Models step (and the Configure accessory
 * list) while content is loading. Each block is wrapped in `animate-pulse`
 * so the user sees motion rather than dead space — the spec calls this out
 * specifically as a perceived-wait improvement.
 *
 * Renders inside a `role="status"` region with an accessible label so
 * assistive tech reads "Loading items" once instead of announcing every
 * placeholder block individually.
 */
export function SkeletonItemCard({ className }: SkeletonItemCardProps) {
  return (
    <Card
      role="status"
      aria-label="Loading model"
      aria-busy="true"
      className={cn("apex-overflow-hidden", className)}
    >
      {/* Image placeholder */}
      <div
        aria-hidden="true"
        className="apex-relative apex-aspect-[4/3] apex-bg-muted apex-animate-pulse"
      />
      <CardContent className="apex-p-4">
        {/* Title */}
        <div
          aria-hidden="true"
          className="apex-h-4 apex-w-3/4 apex-rounded apex-bg-muted apex-animate-pulse"
        />
        {/* Subtitle */}
        <div
          aria-hidden="true"
          className="apex-h-3 apex-w-1/2 apex-rounded apex-bg-muted apex-animate-pulse apex-mt-2"
        />
        {/* Spec tag chips */}
        <div className="apex-flex apex-gap-2 apex-mt-3">
          <div
            aria-hidden="true"
            className="apex-h-5 apex-w-16 apex-rounded apex-bg-muted apex-animate-pulse"
          />
          <div
            aria-hidden="true"
            className="apex-h-5 apex-w-20 apex-rounded apex-bg-muted apex-animate-pulse"
          />
        </div>
        {/* Price + CTA row */}
        <div className="apex-mt-4 apex-flex apex-items-center apex-justify-between">
          <div
            aria-hidden="true"
            className="apex-h-5 apex-w-20 apex-rounded apex-bg-muted apex-animate-pulse"
          />
          <div
            aria-hidden="true"
            className="apex-h-7 apex-w-24 apex-rounded-md apex-bg-muted apex-animate-pulse"
          />
        </div>
      </CardContent>
    </Card>
  );
}
